import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { EventType, EventTypeCategory } from '../../../../../../models/event-type';
import {
  getMembershipIntervalDisplaySingle,
  isSubMonthlyMembershipInterval,
  Membership,
  MembershipEntryTimeSlot
} from '../../../../../../models/membership';
import { MembershipService } from '../../../../../../services/membership/membership.service';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { getRandomNegativeId, propExists } from '../../../../../../utils';
import { VoucherType } from '../../../admin/memberships/membership-detail/membership-detail.component';
import { LightspeedCustomerGroup, SquareCustomerGroup } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { GuestPassEligibility } from '../../../../../../models/customer';
import { TaxRate } from '../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../services/tax-rate/tax-rate.service';
import { SelectLocationModalComponent } from '../../../modals/select-location-modal/select-location-modal.component';
import { CapitanLocation } from '../../../../../../models/location';
import { LocationService } from '../../../../../../services/location/location.service';
import { MembershipInterval } from '../../../../../../models/membership-enums';
import { OrgPOSSoftware } from '../../../../../../models/organization-config-enums';

@Component({
  selector: 'app-event-type-pricing-membership',
  templateUrl: './event-type-pricing-membership.component.html',
  styleUrls: ['./event-type-pricing-membership.component.css']
})
export class EventTypePricingMembershipComponent implements OnInit, ComponentHasUnsavedChanges {

  public readonly SUBMIT_TYPE_STAY_HERE = 'stayHere';
  public readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public eventType: EventType = null;
  public membership: Membership = null;
  public entryTimeSlots: MembershipEntryTimeSlot[] = [];
  public entryTimeSlotUrlsToDelete: string[] = [];
  public voucherType = VoucherType.none;
  public hasIcon = false;
  public submitType = this.SUBMIT_TYPE_STAY_HERE;

  // If this is an existing membership, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the membership object because then any restricted
  // actions would take effect as soon as the status is changed locally, rather
  // than upon saving.
  public hasPerLocationTaxRates = false;
  public hasPerLocationInitiationFeeTaxRates = false;

  public taxRates: TaxRate[];
  public eventTypes: EventType[];
  public eventCategories: EventTypeCategory[];
  public squareCustomerGroups: SquareCustomerGroup[];
  public lightspeedCustomerGroups: LightspeedCustomerGroup[];

  public errorMessage = '';
  public errors: any = {};
  public entryTimeSlotErrors: {[key: number]: any} = {};
  public successMessage = '';
  public unsavedChanges = false;
  public unsavedVoucherChanges = false;

  public loadingEventType = true;
  public loadingMembership = true;
  public loadingExistingEntryTimeSlots = false;
  public loadingTaxRates = true;
  public loadingEventTypes = true;
  public loadingEventCategories = true;
  public loadingSquareCustomerGroups = true;
  public loadingLightspeedCustomerGroups = true;
  public loadingSave = false;

  public notFound = false;
  public serverError: number = null;

  public readonly MembershipInterval = MembershipInterval;
  public readonly GuestPassEligibility = GuestPassEligibility;
  public readonly EventTypeStatus = EventTypeStatus;
  public readonly VoucherType = VoucherType;
  public readonly OrgPOSSoftware = OrgPOSSoftware;
  public readonly getMembershipIntervalDisplaySingle = getMembershipIntervalDisplaySingle;
  public readonly isSubMonthlyMembershipInterval = isSubMonthlyMembershipInterval;

  // Accessed via getLocations()
  private locations: CapitanLocation[] = null;

  constructor(
    public orgConfig: OrganizationConfigService,
    private locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private eventTypeService: EventTypeService,
    private membershipService: MembershipService,
    private taxRateService: TaxRateService,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.notFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadTaxRates();
    this.loadEventTypes();
    this.loadEventCategories();
    this.loadSquareCustomerGroups();
    this.loadLightspeedCustomerGroups();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRates = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoading() {
    return (
      this.loadingEventType ||
      this.loadingMembership
    );
  }

  public removeEntryTimeSlot(timeSlot: MembershipEntryTimeSlot) {
    // If the entry time slot has a positive ID, then it is present in the API
    // and must be deleted.
    if (timeSlot.id > 0) {
      this.entryTimeSlotUrlsToDelete.push(timeSlot.url);
    }

    this.entryTimeSlots.splice(
      this.entryTimeSlots.indexOf(timeSlot),
      1
    );
  }

  /**
   * Whether all days are available for the given time slot.
   */
  public allDaysAreAvailable(timeSlot: MembershipEntryTimeSlot) {
    return (
      timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    );
  }

  /**
   * Set all days to be available or unavailable for the given time slot.
   */
  public setAllDaysAvailability(timeSlot: MembershipEntryTimeSlot, isAvailable: boolean) {
    timeSlot.is_available_sunday = isAvailable;
    timeSlot.is_available_monday = isAvailable;
    timeSlot.is_available_tuesday = isAvailable;
    timeSlot.is_available_wednesday = isAvailable;
    timeSlot.is_available_thursday = isAvailable;
    timeSlot.is_available_friday = isAvailable;
    timeSlot.is_available_saturday = isAvailable;
  }

  /**
   * Add a new entry time slot for the membership.
   */
  public addNewEntryTimeSlot() {
    this.entryTimeSlots.push({
      id: getRandomNegativeId(),
      url: '',
      start_time: '',
      end_time: '',
      is_available_sunday: false,
      is_available_monday: false,
      is_available_tuesday: false,
      is_available_wednesday: false,
      is_available_thursday: false,
      is_available_friday: false,
      is_available_saturday: false,
      created_at: '',
      updated_at: ''
    });
  }

  public resyncSquareCustomerGroups() {
    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software !== OrgPOSSoftware.square)) {
      return;
    }

    this.loadingSquareCustomerGroups = true;

    this.orgConfig
      .syncSquareCustomerGroups()
      .then(() => {
        return this.loadSquareCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingSquareCustomerGroups = false; });
  }

  public resyncLightspeedCustomerGroups() {
    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software !== OrgPOSSoftware.lightspeed)) {
      return;
    }

    this.loadingLightspeedCustomerGroups = true;

    this.orgConfig
      .syncLightspeedCustomerGroups()
      .then(() => {
        return this.loadLightspeedCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingLightspeedCustomerGroups = false; });
  }

  public eventTypeIdInList(eventTypeId: number) {
    for (const eventType of this.eventTypes) {
      if (eventType.id === eventTypeId) { return true; }
    }
    return false;
  }

  public openAddAllowedLocationModal() {
    const modalRef = this.modalService.open(SelectLocationModalComponent);
    modalRef.componentInstance.excludeIds = this.membership
      .allowed_locations.map(loc => loc.id);
    modalRef.componentInstance.submitCallback = (result: CapitanLocation) => {
      this.membership.allowed_locations.push({
        id: result.id,
        name: result.name
      });
      this.unsavedChanges = true;
    }
  }

  public onSubmit() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};
    this.entryTimeSlotErrors = {};

    if (this.voucherType === VoucherType.none) {
      this.membership.voucher_type_id = null;
      this.membership.voucher_category_id = null;
      this.membership.num_vouchers = null;
    } else if (this.voucherType === VoucherType.eventType) {
      this.membership.voucher_category_id = null;
    } else if (this.voucherType === VoucherType.eventCategory) {
      this.membership.voucher_type_id = null;
    }

    // Replace blank discounts before saving.
    if (this.membership.is_group) {
      for (const adultDiscount of this.membership.adult_discounts) {
        // @ts-ignore
        if (!propExists(adultDiscount.discount_amount) || (adultDiscount.discount_amount === '')) {
          adultDiscount.discount_amount = 0;
        }
      }
      if (this.membership.has_youth_tier) {
        for (const youthDiscount of this.membership.youth_discounts) {
          // @ts-ignore
          if (!propExists(youthDiscount.discount_amount) || (youthDiscount.discount_amount === '')) {
            youthDiscount.discount_amount = 0;
          }
        }
      }
    }

    if (!this.hasPerLocationTaxRates) {
      this.membership.tax_rate_per_location = {};
    }
    if (!this.hasPerLocationInitiationFeeTaxRates) {
      this.membership.tax_rate_initiation_fee_per_location = {};
    }

    if (!this.hasIcon) {
      this.membership.icon_color_hex = '';
      this.membership.icon_initials = '';
    }

    // The date picker emits '' when cleared; the API needs null.
    if (this.membership.cancellation_date_range_start === '') {
      this.membership.cancellation_date_range_start = null;
    }
    if (this.membership.cancellation_date_range_end === '') {
      this.membership.cancellation_date_range_end = null;
    }

    // Save the membership.
    this.membershipService
      .updateMembership(this.membership)
      .then(savedMembership => {
        this.membership = savedMembership;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to configure this rolling membership. The "Organization Admin" permission is required to edit memberships.';
        }

        return Promise.reject(reason);
      })
      .then(() => {
        return this.saveTimeSlots();
      })
      .then(() => {
        this.unsavedChanges = false;
        this.unsavedVoucherChanges = false;

        // Routing depends on which save button was clicked.
        if (this.submitType === this.SUBMIT_TYPE_STAY_HERE) {
          this.successMessage = 'Pricing updated successfully.';
        } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
          this.router.navigate(
            ['/events/setup/edit/' + this.eventType.id.toString() + '/rosters']
          );
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          // Do nothing: this is handled in the individual promises.
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify this membership.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  public getLocations(): CapitanLocation[] {
    if (!this.locations) {
      this.locations = this.locationService.getLocations();
    }
    return this.locations;
  }

  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
        this.loadMembership(this.eventType.pricing_membership_id);
        this.loadEntryTimeSlots(this.eventType.pricing_membership_id);
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadMembership(membershipId: number) {
    this.loadingMembership = true;

    this.membershipService
      .getMembership(membershipId)
      .then(membership => {
        this.membership = membership;
        if (this.membership.voucher_type_id) {
          this.voucherType = VoucherType.eventType;
        }
        if (this.membership.voucher_category_id) {
          this.voucherType = VoucherType.eventCategory;
        }
        if (Object.keys(this.membership.tax_rate_per_location).length > 0) {
          this.hasPerLocationTaxRates = true;
        }
        if (Object.keys(this.membership.tax_rate_initiation_fee_per_location).length > 0) {
          this.hasPerLocationInitiationFeeTaxRates = true;
        }
        if (this.membership.icon_color_hex !== '') {
          this.hasIcon = true;
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMembership = false; });
  }

  /**
   * Load the entry time slots for the membership with the given ID (used for
   * editing existing memberships).
   */
  private loadEntryTimeSlots(membershipId: number) {
    this.loadingExistingEntryTimeSlots = true;

    this.membershipService
      .getMembershipEntryTimeSlots(membershipId, 1, 100)
      .then(entryTimeSlotsResponse => {
        this.entryTimeSlots = entryTimeSlotsResponse.results;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingEntryTimeSlots = false; });
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {status: EventTypeStatus.active}
      )
      .then(response => {
        this.eventTypes = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(
        1,
        3000
      )
      .then(response => {
        this.eventCategories = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        this.loadingEventCategories = false;
      });
  }

  private loadSquareCustomerGroups() {
    this.loadingSquareCustomerGroups = true;

    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software === OrgPOSSoftware.square)) {
      this.orgConfig
        .getSquareCustomerGroups()
        .then(response => {
          this.squareCustomerGroups = response.filter(cg => cg.is_managed_by_capitan);
        })
        .catch(reason => {
          this.serverError = reason.status;
        })
        .finally(() => { this.loadingSquareCustomerGroups = false; });
    } else {
      this.squareCustomerGroups = [];
      this.loadingSquareCustomerGroups = false;
    }
  }

  private loadLightspeedCustomerGroups() {
    this.loadingLightspeedCustomerGroups = true;

    const orgConfigObj = this.orgConfig.getOrganizationConfig();
    if ((orgConfigObj === null) || (orgConfigObj.pos_software === OrgPOSSoftware.lightspeed)) {
      this.orgConfig
        .getLightspeedCustomerGroups()
        .then(response => {
          this.lightspeedCustomerGroups = response
            .filter(cg => cg.is_managed_by_capitan)
            .sort((a, b) => a.name < b.name ? -1 : 1);
        })
        .catch(reason => {
          this.serverError = reason.status;
        })
        .finally(() => { this.loadingLightspeedCustomerGroups = false; });
    } else {
      this.lightspeedCustomerGroups = [];
      this.loadingLightspeedCustomerGroups = false;
    }
  }

  private saveTimeSlots(): Promise<any>[] {
    const savePromises: Promise<any>[] = [];

    // Save the membership time slots.
    for (const timeSlotToSave of this.entryTimeSlots) {
      let timeSlotSavePromise: Promise<MembershipEntryTimeSlot>;
      if (timeSlotToSave.id > 0) {
        timeSlotSavePromise = this.membershipService.updateMembershipEntryTimeSlot(timeSlotToSave);
      } else {
        timeSlotSavePromise = this.membershipService.addMembershipEntryTimeSlot(this.membership.id, timeSlotToSave);
      }
      timeSlotSavePromise = timeSlotSavePromise.catch(reason => {
        if (reason.status === 400) {
          this.entryTimeSlotErrors[timeSlotToSave.id] = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        }

        return Promise.reject(reason);
      });
      savePromises.push(timeSlotSavePromise);
    }

    // Delete the removed membership time slots.
    for (const timeSlotUrlToDelete of this.entryTimeSlotUrlsToDelete) {
      savePromises.push(
        this.membershipService.deleteMembershipEntryTimeSlot(timeSlotUrlToDelete)
      );
    }

    return savePromises;
  }

}
